// src/api/loans.js
import { API_BASE } from "./auth";

/**
 * Loan API Service
 * All functions require an authenticated axios client (api)
 */

/**
 * Calculate loan installment (preview)
 * GET /api/loans/calculate
 */
export async function calculateLoan(api, { loan_amount, tenor_months, interest_rate = 2.0 }) {
  const params = new URLSearchParams({
    loan_amount: loan_amount.toString(),
    tenor_months: tenor_months.toString(),
    interest_rate: interest_rate.toString(),
  });
  const { data } = await api.get(`/loans/calculate?${params}`);
  return data;
}

/**
 * Get list of loans with filters
 * GET /api/loans
 */
export async function getLoans(api, filters = {}) {
  const params = new URLSearchParams();
  if (filters.member_id) params.append('member_id', filters.member_id);
  if (filters.status) params.append('status', filters.status);
  if (filters.loan_type) params.append('loan_type', filters.loan_type);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.search) params.append('search', filters.search);
  if (filters.page) params.append('page', filters.page);
  
  const queryString = params.toString();
  const { data } = await api.get(`/loans${queryString ? '?' + queryString : ''}`);
  return data;
}

/**
 * Get loan details by ID
 * GET /api/loans/{id}
 */
export async function getLoanById(api, id) {
  const { data } = await api.get(`/loans/${id}`);
  return data;
}

/**
 * Create new loan application
 * POST /api/loans
 */
export async function createLoan(api, loanData) {
  const { data } = await api.post('/loans', loanData);
  return data;
}

/**
 * Update loan (draft only)
 * PUT /api/loans/{id}
 */
export async function updateLoan(api, id, loanData) {
  const { data } = await api.put(`/loans/${id}`, loanData);
  return data;
}

/**
 * Delete loan (draft only)
 * DELETE /api/loans/{id}
 */
export async function deleteLoan(api, id) {
  const { data } = await api.delete(`/loans/${id}`);
  return data;
}

/**
 * Submit loan application for approval
 * POST /api/loans/{id}/submit
 */
export async function submitLoan(api, id) {
  const { data } = await api.post(`/loans/${id}/submit`);
  return data;
}

/**
 * Approve loan (admin/pegawai only)
 * POST /api/loans/{id}/approve
 */
export async function approveLoan(api, id) {
  const { data } = await api.post(`/loans/${id}/approve`);
  return data;
}

/**
 * Reject loan (admin/pegawai only)
 * POST /api/loans/{id}/reject
 */
export async function rejectLoan(api, id, rejection_reason) {
  const { data } = await api.post(`/loans/${id}/reject`, { rejection_reason });
  return data;
}

/**
 * Record installment payment (admin/pegawai only)
 * POST /api/loans/{loanId}/installments/{installmentId}/pay
 */
export async function payInstallment(api, loanId, installmentId, paymentData) {
  const { data } = await api.post(
    `/loans/${loanId}/installments/${installmentId}/pay`,
    paymentData
  );
  return data;
}

/**
 * Helper: Format currency to IDR
 */
export function formatCurrency(amount) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(amount || 0);
}

/**
 * Helper: Format date
 */
export function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Helper: Get status badge color
 */
export function getStatusColor(status) {
  const colors = {
    draft: "bg-gray-100 text-gray-700",
    pending: "bg-yellow-100 text-yellow-700",
    approved: "bg-blue-100 text-blue-700",
    active: "bg-green-100 text-green-700",
    completed: "bg-purple-100 text-purple-700",
    rejected: "bg-red-100 text-red-700",
  };
  return colors[status] || "bg-gray-100 text-gray-700";
}

/**
 * Helper: Get installment status badge color
 */
export function getInstallmentStatusColor(status) {
  const colors = {
    pending: "bg-yellow-100 text-yellow-700",
    paid: "bg-green-100 text-green-700",
    partial: "bg-blue-100 text-blue-700",
    overdue: "bg-red-100 text-red-700",
  };
  return colors[status] || "bg-gray-100 text-gray-700";
}
